import React, { useEffect, useRef, useState, useCallback } from "react";
import { Container, Row, Col, Button, ButtonGroup } from "react-bootstrap";
import QRCode from "qrcode";
import { Link } from "react-router-dom";

const SITE_URL = "https://enchanted-gardener.com";

// vCard format for contact QR
const VCARD = `BEGIN:VCARD
VERSION:3.0
N:Gardener;Enchanted;;;
FN:Enchanted Gardener
ORG:Enchanted Gardener
TEL;TYPE=WORK:+1-616-682-0808
EMAIL:info@enchanted-gardener.com
URL:${SITE_URL}
ADR;TYPE=WORK:;;9430 Vergennes St SE;Ada;MI;49301;USA
NOTE:Professional Gardening & Landscape Services - West Michigan
END:VCARD`;

const TABS = {
  website: {
    data: SITE_URL,
    label: "Website",
    filename: "enchanted-gardener-website-qr",
  },
  contact: {
    data: VCARD,
    label: "Add Contact",
    filename: "enchanted-gardener-contact-qr",
  },
};

export default function QR() {
  const canvasRef = useRef(null);
  const [activeTab, setActiveTab] = useState("website");
  const [copied, setCopied] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
  }, []);

  const generateQR = useCallback(async () => {
    if (!canvasRef.current) return;
    try {
      await QRCode.toCanvas(canvasRef.current, TABS[activeTab].data, {
        width: 240,
        margin: 0,
        color: {
          dark: "#2d5a27",
          light: "#ffffff",
        },
      });
    } catch (err) {
      console.error("QR generation failed:", err);
    }
  }, [activeTab]);

  useEffect(() => {
    generateQR();
  }, [generateQR]);

  const downloadPNG = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const size = 1024;
    const downloadCanvas = document.createElement("canvas");
    const ctx = downloadCanvas.getContext("2d");
    if (!ctx) return;

    downloadCanvas.width = size;
    downloadCanvas.height = size;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);

    const padding = 64;
    ctx.drawImage(canvas, padding, padding, size - padding * 2, size - padding * 2);

    const filename = `${TABS[activeTab].filename}.png`;

    downloadCanvas.toBlob(async (blob) => {
      if (!blob) return;

      if (navigator.share && isIOS) {
        try {
          const file = new File([blob], filename, { type: "image/png" });
          await navigator.share({
            files: [file],
            title: `Enchanted Gardener QR Code - ${TABS[activeTab].label}`,
          });
          return;
        } catch (err) {
          if (err.name !== "AbortError") {
            console.log("Share failed, trying alternative method");
          }
        }
      }

      if (isIOS) {
        const dataUrl = downloadCanvas.toDataURL("image/png");
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>QR Code - Long press to save</title></head>
              <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f0;">
                <div style="text-align:center;">
                  <p style="color:#2d5a27;margin-bottom:1rem;font-family:system-ui;">Long press the image to save</p>
                  <img src="${dataUrl}" alt="QR Code" style="max-width:90vw;max-height:80vh;" />
                </div>
              </body>
            </html>
          `);
        }
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const shareOrCopy = async () => {
    const shareData =
      activeTab === "website"
        ? { title: "Enchanted Gardener", text: "Visit our website!", url: SITE_URL }
        : { title: "Enchanted Gardener Contact", text: "Add us as a contact", url: SITE_URL };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6} className="text-center">
            {/* Header */}
            <div style={{ marginBottom: "2rem" }}>
              <h1
                style={{
                  color: "#2d5a27",
                  fontWeight: "600",
                  fontSize: "2rem",
                  marginBottom: "0.5rem",
                  fontFamily: "Georgia, serif",
                }}
              >
                Enchanted Gardener
              </h1>
              <p style={{ color: "#666", fontSize: "0.9rem", margin: 0 }}>QR Code Generator</p>
            </div>

            {/* Tabs */}
            <div style={{ marginBottom: "1.5rem" }}>
              <ButtonGroup>
                {Object.keys(TABS).map((tab) => (
                  <Button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    variant={activeTab === tab ? "success" : "outline-secondary"}
                    style={
                      activeTab === tab
                        ? { backgroundColor: "#2d5a27", borderColor: "#2d5a27" }
                        : {}
                    }
                  >
                    {TABS[tab].label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>

            {/* QR Code */}
            <div
              style={{
                display: "inline-block",
                backgroundColor: "#fff",
                padding: "2rem",
                borderRadius: "1rem",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                marginBottom: "1.5rem",
              }}
            >
              <canvas ref={canvasRef} width={240} height={240} />
            </div>

            {/* Description */}
            <p
              style={{
                color: "#2d5a27",
                fontWeight: "600",
                fontSize: "0.95rem",
                marginBottom: "1.5rem",
              }}
            >
              {activeTab === "website" ? "enchanted-gardener.com" : "Scan to add contact"}
            </p>

            {/* Actions */}
            <div className="d-flex justify-content-center gap-3" style={{ marginBottom: "1.5rem" }}>
              <Button
                onClick={downloadPNG}
                style={{
                  backgroundColor: "#2d5a27",
                  borderColor: "#2d5a27",
                  fontWeight: "600",
                  padding: "0.6rem 1.5rem",
                }}
              >
                {isIOS ? "Save QR Code" : "Download PNG"}
              </Button>

              <Button
                variant="outline-secondary"
                onClick={shareOrCopy}
                style={{ padding: "0.6rem 1.5rem" }}
              >
                {copied ? "Copied!" : "Share"}
              </Button>
            </div>

            {/* Hint */}
            <p style={{ color: "#999", fontSize: "0.8rem", marginBottom: "2rem" }}>
              {activeTab === "website"
                ? "Use this QR code for business cards and marketing materials"
                : "Scanning this QR code will prompt to add Enchanted Gardener as a contact"}
            </p>

            {/* Back link */}
            <Link to="/" style={{ color: "#2d5a27", fontSize: "0.9rem", textDecoration: "none" }}>
              &larr; Back to website
            </Link>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
